/**
 * Registry of AbortControllers keyed by name, so a slice can cancel its
 * previous in-flight request before starting a new one.
 *
 * Usage: `createAbort(key)` cancels the previous request under that key and
 * returns a fresh signal; call `clearAbort(key)` on success, and treat a
 * caught `AbortError` as "superseded, ignore".
 */

export const ABORT_KEYS = {
  TASK_LIST: 'taskList',
  TASK_LIST_SEARCH: 'taskListSearch',
  TASKS_PROGRESS: 'tasksProgress',
  TASK_AISLES: 'taskAisles',
  TASK_IMAGES: 'taskImages',
  TASKS_STATUS: 'tasksStatus',
  PRODUCT_INFO: 'productInfo',
  PRODUCT_CAPTURES: 'productCaptures',
  PRODUCT_BARCODE: 'productBarcode',
  NAVIGATION_STATUS: 'navigationStatus',
  RESOURCES_OVERHEAD: 'resourcesOverhead',
  RESOURCES_TASK_DETAILS_FEATURES: 'resourcesTaskDetailsFeatures',
};

const controllers = new Map();

/**
 * Abort any previous request for `key`, create a new AbortController,
 * register it, and return its signal.
 */
export function createAbort(key) {
  if (controllers.has(key)) {
    controllers.get(key).abort();
  }
  const controller = new AbortController();
  controllers.set(key, controller);
  return controller.signal;
}

/**
 * Remove the controller for `key` once the request has settled.
 * Call this in the success path; errors and aborts self-clean via the Map overwrite.
 */
export function clearAbort(key) {
  controllers.delete(key);
}

/**
 * Abort the current request for `key` without creating a new controller.
 * Use this on unmount/cleanup to avoid leaking unused controllers in the Map.
 */
export function abortOnly(key) {
  if (controllers.has(key)) {
    controllers.get(key).abort();
    controllers.delete(key);
  }
}
